import { useState, useMemo, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useAsset } from "../../context/AssetContext";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import { useAssetApi } from "../../api/useAssetApi";

const AssetIdEditModal: React.FC = () => {
  const { asset, systemCode, isEditAssetIdModalOpen, closeEditAssetIdModal } = useAsset();
  const { user } = useSession(); 
  const { updateAssetIdApi } = useAssetApi();
  const navigate = useNavigate();

  const oldAssetId = useMemo(() => {
    if (!asset) return "";
    return asset.assetId;
  }, [asset]);

  const [newAssetId, setNewAssetId] = useState(oldAssetId);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when the modal opens or asset changes
  useEffect(() => {
    if (isEditAssetIdModalOpen) {
      setNewAssetId(oldAssetId);
      setReason("");
      setError(null);
    }
  }, [isEditAssetIdModalOpen, oldAssetId]);


  const isSubmitDisabled = useMemo(() => {
    return newAssetId.trim() === oldAssetId.trim() || !reason.trim() || isSubmitting;
  }, [newAssetId, reason, oldAssetId, isSubmitting]);

  const handleSubmit = async () => {
    if (isSubmitDisabled || !asset) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        pk: asset.pk,
        oldAssetId: oldAssetId.trim(),
        newAssetId: newAssetId.trim(),
        user,
        reason: reason.trim(),
        updateTime: new Date().toISOString(),
      };

      await updateAssetIdApi(payload);
      const timestamp = asset.pk.split('-').pop();
      const newFullAssetId = `${newAssetId.trim()}-${timestamp}`;

      // Navigate to the new asset page
      console.log("should be navigating to ", `asset/${systemCode}/${newFullAssetId}`)
      navigate(`/asset/${systemCode}/${newFullAssetId}`);
      closeEditAssetIdModal();
    } catch (err) {
      console.error("Error updating asset ID:", err);
      setError("Failed to update Asset ID. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssetIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewAssetId(e.target.value.toUpperCase());
  };

  if (!asset) return null;

  return (
    <Dialog open={isEditAssetIdModalOpen} onClose={closeEditAssetIdModal} fullWidth maxWidth="sm">
      <DialogTitle>Edit Asset ID</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="textSecondary">
            Current Asset ID: {oldAssetId}
          </Typography>
          <TextField
            data-cy="edit-asset-id-input"
            autoFocus
            margin="dense"
            label="New Asset ID"
            type="text"
            fullWidth
            value={newAssetId}
            onChange={handleAssetIdChange}
            inputProps={{
              style: { textTransform: "uppercase" },
            }}
          />
          <TextField
            data-cy="edit-asset-id-reason-input"
            margin="dense"
            label="Reason for change"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-cy="edit-asset-id-cancel-btn" onClick={closeEditAssetIdModal} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button data-cy="edit-asset-id-submit-btn" onClick={handleSubmit} variant="contained" disabled={isSubmitDisabled}>
          {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetIdEditModal;
